/**
 * MFA Admin Helper Utilities
 * Helper functions for administrators to manage MFA setup via email
 */

import { appApi } from '@/infrastructure/api';

export interface AdminMFASetupRequest {
  email: string;
  adminName: string;
}

export interface AdminMFAStatus {
  needsMFASetup: boolean;
  isAdmin: boolean;
  clearanceLevel: string;
  hasTopSecretClearance: boolean;
}

/**
 * Request MFA setup email for an administrator
 */
export async function requestAdminMFASetup(request: AdminMFASetupRequest): Promise<{
  success: boolean;
  message?: string;
  setupUrl?: string;
  expiresAt?: string;
  error?: string;
}> {
  try {
    console.log('MFA Admin Helper: Requesting MFA setup for:', request.email);

    const response = await fetch('/api/auth/request-mfa-setup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    const data = await response.json();

    if (data.success) {
      console.log('MFA Admin Helper: Setup email sent successfully');
      return {
        success: true,
        message: 'MFA setup email sent successfully',
        setupUrl: data.setupUrl,
        expiresAt: data.expiresAt
      };
    } else {
      console.error('MFA Admin Helper: Failed to send setup email:', data.error);
      return {
        success: false,
        error: data.error || 'Failed to send MFA setup email'
      };
    }
  } catch (error) {
    console.error('MFA Admin Helper: Error requesting MFA setup:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Check if an administrator needs MFA setup
 */
export async function checkAdminMFAStatus(email: string): Promise<AdminMFAStatus | null> {
  try {
    const response = await fetch(`/api/auth/request-mfa-setup?email=${encodeURIComponent(email)}`, {
      method: 'GET'
    });

    const data = await response.json();

    if (data.success) {
      return {
        needsMFASetup: data.needsMFASetup,
        isAdmin: data.isAdmin,
        clearanceLevel: data.clearanceLevel,
        hasTopSecretClearance: data.clearanceLevel === 'top_secret'
      };
    }

    return null;
  } catch (error) {
    console.error('MFA Admin Helper: Error checking MFA status:', error);
    return null;
  }
}

/**
 * Send MFA setup email to the current administrator (self-service)
 */
export async function requestSelfMFASetup(adminEmail: string, adminName: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    // First check if the admin needs MFA setup
    const status = await checkAdminMFAStatus(adminEmail);

    if (!status) {
      return {
        success: false,
        error: 'Unable to verify administrator status'
      };
    }

    if (!status.isAdmin) {
      return {
        success: false,
        error: 'MFA setup is only available for administrators'
      };
    }

    if (!status.needsMFASetup) {
      return {
        success: false,
        error: 'MFA is already configured for this account'
      };
    }

    // Request MFA setup email
    const result = await requestAdminMFASetup({
      email: adminEmail,
      adminName
    });

    return result;
  } catch (error) {
    console.error('MFA Admin Helper: Error in self MFA setup:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Generate emergency MFA setup for locked-out administrators
 * This should be used when an admin is locked out due to MFA requirements
 */
export async function generateEmergencyMFASetup(adminEmail: string): Promise<{
  success: boolean;
  setupUrl?: string;
  message?: string;
  error?: string;
}> {
  try {
    console.log('MFA Admin Helper: Generating emergency MFA setup for:', adminEmail);

    // This would typically require additional verification in production
    const response = await fetch('/api/auth/emergency-mfa-setup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: adminEmail,
        emergency: true,
        reason: 'Administrator locked out due to MFA requirement'
      })
    });

    const data = await response.json();

    if (data.success) {
      console.log('MFA Admin Helper: Emergency setup generated successfully');
      return {
        success: true,
        setupUrl: data.setupUrl,
        message: 'Emergency MFA setup email sent. Check your email for setup instructions.'
      };
    } else {
      return {
        success: false,
        error: data.error || 'Failed to generate emergency MFA setup'
      };
    }
  } catch (error) {
    console.error('MFA Admin Helper: Error generating emergency MFA setup:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Validate if current session allows MFA setup bypass for emergency access
 */
export function canBypassMFAForSetup(): boolean {
  // In development, allow bypass
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  // In production, this would require additional security checks
  // such as IP whitelisting, time-based tokens, or manual approval
  return false;
}

/**
 * Get MFA setup instructions for manual configuration
 */
export function getMFASetupInstructions(): {
  title: string;
  steps: string[];
  apps: { name: string; ios?: string; android?: string; }[];
} {
  return {
    title: 'MFA Setup Instructions',
    steps: [
      'Download a compatible authenticator app on your mobile device',
      'Click the MFA setup link in your email',
      'Scan the QR code with your authenticator app',
      'Enter the 6-digit verification code from your app',
      'Download and securely store your backup codes',
      'Complete the setup process'
    ],
    apps: [
      {
        name: 'Google Authenticator',
        ios: 'https://apps.apple.com/app/google-authenticator/id388497605',
        android: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
      },
      {
        name: 'Microsoft Authenticator',
        ios: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
        android: 'https://play.google.com/store/apps/details?id=com.azure.authenticator'
      },
      {
        name: 'Authy',
        ios: 'https://apps.apple.com/app/authy/id494168017',
        android: 'https://play.google.com/store/apps/details?id=com.authy.authy'
      }
    ]
  };
}
